import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, ArrowLeft, Globe, Send, Loader2, Building2, Users, Mail, Phone, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const navigate = useNavigate();
  const { t, isRTL, language, setLanguage, supportedLanguages } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    employees: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.message) {
      toast.error(t("fillAllFields") || "Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(API_URL + "/api/contact/enterprise", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success(t("messageSent") || "Message sent successfully!");
      } else {
        toast.error(t("messageFailed") || "Failed to send message");
      }
    } catch (error) {
      toast.error(t("messageFailed") || "Failed to send message");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className={`min-h-screen bg-background`} dir={isRTL ? 'rtl' : 'ltr'}>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
            <Link to="/" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <MapPin className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-heading font-bold text-xl text-foreground">{t("appName")}</span>
            </Link>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px]">
                <Globe className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </nav>

        <main className="pt-24 pb-16 px-4 md:px-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-4">{t("thankYou") || "Thank You!"}</h1>
            <p className="text-muted-foreground mb-8">
              {t("contactSuccess") || "Your message has been sent successfully. Our team will contact you within 24-48 hours."}
            </p>
            <Button onClick={() => navigate("/")}>{t("backToHome") || "Back to Home"}</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background`} dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">{t("appName")}</span>
          </Link>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px]">
                <Globe className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/">
              <Button variant="ghost" size="sm" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />{t("backToHome")}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">{t("contactTitle") || "Contact Us"}</h1>
            <p className="text-lg text-muted-foreground">
              {t("contactSubtitle") || "Get in touch with our team for Enterprise solutions"}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Mail className="w-5 h-5" />
                  {t("sendMessage") || "Send us a Message"}
                </CardTitle>
                <CardDescription>
                  {t("contactFormDesc") || "Fill out the form and we'll get back to you within 24-48 hours."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("fullName") || "Full Name"} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t("enterName") || "Enter your name"}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t("email") || "Email"} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t("enterEmail") || "Enter your email"}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">{t("companyName") || "Company Name"} *</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder={t("enterCompany") || "Enter company name"}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t("phone") || "Phone"}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder={t("enterPhone") || "+966 xxx xxx xxxx"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employees">{t("employeeCount") || "Number of Employees"}</Label>
                      <Select value={formData.employees} onValueChange={(val) => setFormData({ ...formData, employees: val })}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("selectRange") || "Select range"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="100-250">100-250</SelectItem>
                          <SelectItem value="250-500">250-500</SelectItem>
                          <SelectItem value="500-1000">500-1000</SelectItem>
                          <SelectItem value="1000+">1000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t("message") || "Message"} *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={t("enterMessage") || "Tell us about your needs..."}
                      rows={4}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <Loader2 className={`w-4 h-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    ) : (
                      <Send className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    )}
                    {t("sendMessage") || "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Building2 className="w-5 h-5" />
                    {t("enterpriseBenefits") || "Enterprise Benefits"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <Users className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">{t("unlimitedEmployees") || "Unlimited Employees"}</p>
                        <p className="text-sm text-muted-foreground">{t("unlimitedEmployeesDesc") || "No limits on team size"}</p>
                      </div>
                    </li>
                    <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <Mail className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">{t("dedicatedSupport") || "Dedicated Support"}</p>
                        <p className="text-sm text-muted-foreground">{t("dedicatedSupportDesc") || "Priority support with dedicated account manager"}</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("directContact") || "Direct Contact"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <a href="mailto:geocheck.inc@gmail.com" className="text-primary hover:underline">
                      geocheck.inc@gmail.com
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
